import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "@/components/Logo";
import SkipToContent from "@/components/SkipToContent";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>מדיניות פרטיות | אמון בזוגיות</title>
        <meta
          name="description"
          content="מדיניות הפרטיות של אמון בזוגיות - איך אנו אוספים, משתמשים ומגנים על המידע שלך."
        />
      </Helmet>

      <SkipToContent />

      <div className="min-h-screen bg-background" dir="rtl">
        {/* Header */}
        <header role="banner" className="bg-navy py-4">
          <div className="container mx-auto px-4 flex justify-between items-center">
            <Logo variant="light" />
            <Link
              to="/"
              className="text-cream hover:text-gold transition-colors"
              aria-label="חזרה לדף הבית"
            >
              חזרה לדף הבית
            </Link>
          </div>
        </header>

        {/* Content */}
        <main id="main-content" role="main" tabIndex={-1} className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold text-navy mb-8">מדיניות פרטיות</h1>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground">
            <p className="text-muted-foreground">
              עודכן לאחרונה: {new Date().toLocaleDateString("he-IL")}
            </p>

            <section aria-labelledby="info-collected-heading" className="space-y-4">
              <h2 id="info-collected-heading" className="text-2xl font-semibold text-navy">
                1. מידע שאנו אוספים
              </h2>
              <p>
                בעת מילוי טופס יצירת הקשר באתר, אנו אוספים את המידע הבא:
              </p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>
                  <strong>שם מלא</strong> - לצורך פנייה אישית אליך
                </li>
                <li>
                  <strong>מספר טלפון</strong> - ליצירת קשר ראשוני ותיאום פגישת
                  היכרות
                </li>
              </ul>
            </section>

            <section aria-labelledby="info-use-heading" className="space-y-4">
              <h2 id="info-use-heading" className="text-2xl font-semibold text-navy">
                2. כיצד אנו משתמשים במידע
              </h2>
              <p>המידע שנאסף משמש אך ורק למטרות הבאות:</p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>יצירת קשר ראשוני לתיאום פגישת היכרות</li>
                <li>מענה לשאלות ובקשות מידע</li>
                <li>מתן מידע על השירותים שלנו</li>
              </ul>
              <p>
                <strong>
                  אנו לא משתפים את המידע שלך עם צדדים שלישיים
                </strong>{" "}
                למטרות שיווקיות או לכל מטרה אחרת.
              </p>
            </section>

            <section aria-labelledby="security-heading" className="space-y-4">
              <h2 id="security-heading" className="text-2xl font-semibold text-navy">
                3. אבטחת מידע
              </h2>
              <p>
                אנו נוקטים באמצעי אבטחה סבירים כדי להגן על המידע האישי שלך
                מפני גישה בלתי מורשית, שימוש לרעה או חשיפה.
              </p>
            </section>

            <section aria-labelledby="retention-heading" className="space-y-4">
              <h2 id="retention-heading" className="text-2xl font-semibold text-navy">
                4. שמירת מידע
              </h2>
              <p>
                המידע שלך נשמר כל עוד הוא נדרש לצורך מתן השירות או עד
                לבקשתך למחיקתו.
              </p>
            </section>

            <section aria-labelledby="rights-heading" className="space-y-4">
              <h2 id="rights-heading" className="text-2xl font-semibold text-navy">
                5. הזכויות שלך
              </h2>
              <p>יש לך זכות:</p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>לבקש עיון במידע שנאסף עליך</li>
                <li>לבקש תיקון מידע שגוי</li>
                <li>לבקש מחיקת המידע שלך</li>
              </ul>
            </section>

            <section aria-labelledby="contact-heading" className="space-y-4">
              <h2 id="contact-heading" className="text-2xl font-semibold text-navy">
                6. יצירת קשר
              </h2>
              <p>
                לכל שאלה בנוגע למדיניות הפרטיות או לבקשות הקשורות למידע
                האישי שלך, ניתן ליצור קשר באמצעות הוואטסאפ או טופס יצירת
                הקשר באתר.
              </p>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer role="contentinfo" className="bg-navy text-cream py-8 mt-16">
          <div className="container mx-auto px-4 text-center space-y-4">
            <p>© {new Date().getFullYear()} אמון בזוגיות. כל הזכויות שמורות.</p>
            <nav aria-label="קישורי פוטר">
              <ul className="flex justify-center gap-6 text-sm">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-gold transition-colors"
                    aria-current="page"
                  >
                    מדיניות פרטיות
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="hover:text-gold transition-colors">
                    הצהרת נגישות
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicy;
